import React, { useContext } from "react";
import PropTypes from "prop-types";
import { prProfileUrl, companyUrl, toAbsoluteUrl } from "views_shared/app/javascript/utils/urls";
import AvatarProfileImage from "../../AvatarProfileImage";
import PersonHeader from "../../profiles/PersonHeader";
import ProfileHeaderLocation from "../../profiles/ProfileHeaderLocation";
import Qwoted100Badge from "../../profiles/Qwoted100Badge";
import useBadge from "../../profiles/use_badge";
import FollowControl from "../../profiles/FollowControl";
import ShareProfileButton from "../../profiles/ShareProfileButton";
import HeaderKebabMenu from "../../profiles/HeaderKebabMenu";
import MessageButton from "../../MessageButton";
import { PrProfileContext } from "../data/pr_profile_context_management";

const prProfileBannerImage = RAILS_ASSET_URL("/pr-profile-banner.png");

// A small inline edit pen for the header's name/headline (the affordances Ivan had next to the
// name and title). Renders only when the viewer may edit; opens the given section's modal.
function HeaderEditPen({ section, label }) {
  const { viewerContext, setOpenEditModal } = useContext(PrProfileContext);
  if (!viewerContext?.editable) return null;
  return (
    <button
      type="button"
      aria-label={label}
      className="btn btn-primary btn-sm ms-2 d-inline-flex align-items-center justify-content-center"
      style={{ width: "2rem", height: "2rem", padding: 0 }}
      onClick={() => setOpenEditModal(section)}
    >
      <i className="fa-solid fa-pencil-alt" />
    </button>
  );
}

HeaderEditPen.propTypes = {
  section: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

// PR Profile's engine header: composes the shared <PersonHeader> chrome from the PR record — a
// banner band, the avatar, the identity column (name + featured title + headline + badges, with
// inline edit pens), and a top-right actions row. The featured title (`title_at_company`) is
// denormalized on the pr_profiles materialized view, so the header needs no employment fetch; the
// full employment list lives in its own Employment section (see pr_sections). The bio lives in its
// own About section card. Contact details and the message action live in the rail's Connect card
// (see PrProfile), not the header.
function PrPersonHeader() {
  const { prProfile, prProfileId, viewerContext, setOpenEditModal, silentReload } = useContext(PrProfileContext);
  const isQwoted100Member = useBadge("qwoted100", "Id::Profile::Promoter", prProfile.id);
  const editable = !!viewerContext?.editable;

  // <AvatarWithLabel> stays the visual renderer; <AvatarProfileImage> only adds the hover-revealed
  // edit controls around it, and only for a viewer who may edit. imgOwnerId is prProfileId (the
  // Id::Profile id the /user_profiles/:id/profile_image route expects), not a promo-entity id —
  // silentReload is the same "this saved, refetch" signal the rest of the edit surface uses.
  const avatar = (
    <AvatarProfileImage
      profileType="promoter_pr"
      avatarClassName="border border-info border-4"
      size={160}
      editable={editable}
      imgOwnerId={prProfileId}
      imgOwnerName={prProfile.name}
      imgOwnerType="user_profile"
      profileImageUrl={prProfile.profile_image_url}
      onChange={silentReload}
    />
  );

  const banner = (
    <div className="person-header-banner">
      <img src={prProfileBannerImage} alt="" />
    </div>
  );

  // One headline slot: custom headline > featured job title > most recent job title. The view
  // coalesces headline onto the same title string when no custom headline is set, so a headline
  // that differs from that string (trimmed) is custom and takes the slot alone.
  const hasCustomHeadline =
    !!(prProfile.headline || "").trim() &&
    (prProfile.headline || "").trim() !== (prProfile.title_at_company || "").trim();
  const hasLinkedTitle = !!(prProfile.title && prProfile.company_slug && prProfile.company_name);

  // Ruled identity-stack order: name+pen -> headline -> location -> badges row. Location is
  // render-when-present: the enriched view coalesces the self-expert entity's over the promoter's
  // own, so a hybrid shows its Expert's and a pure PR its own. The Qwoted 100 pill sits in the
  // badges row at the bottom.
  const identity = (
    <>
      <h1 className="h3 fw-bold mb-1">
        {prProfile.name}
        <HeaderEditPen section="HEADER" label="Edit Header" />
      </h1>
      {hasCustomHeadline && <h2 className="h5 text-body-secondary mb-2">{prProfile.headline}</h2>}
      {/* PR reps are company-anchored, so the derived title links out to the workplace's company page
          when title + company columns are all present. Otherwise the pre-joined title_at_company, which the
          view already composes down to just the company name (no " at ") when the job title is blank. The
          view can also compose that fallback down to an empty string when the person has no job at all, so
          the heading itself is guarded rather than always rendered around whichever branch resolves. */}
      {!hasCustomHeadline && (hasLinkedTitle || prProfile.title_at_company) && (
        <h2 className="h5 text-body-secondary mb-2">
          {hasLinkedTitle ? (
            <>
              {prProfile.title} at{" "}
              <a href={companyUrl({ slug: prProfile.company_slug, migrate_id: prProfile.company_migrate_id })}>
                {prProfile.company_name}
              </a>
            </>
          ) : (
            prProfile.title_at_company
          )}
        </h2>
      )}
      <ProfileHeaderLocation location={prProfile.location_string} />
      {isQwoted100Member && (
        <div className="mt-2">
          <Qwoted100Badge />
        </div>
      )}
    </>
  );

  const canonicalProfileUrl = toAbsoluteUrl(prProfileUrl({ slug: prProfile.slug, migrate_id: prProfile.migrate_id }));

  // Viewer-split actions row (below the banner, top-right): a viewer of someone else's profile gets
  // [Follow] [Share Profile]; an owner/admin gets [Share Profile] [kebab] instead — the kebab never
  // renders for a non-editor, so it never ships empty to the public. Both kebab items open the same
  // header edit modal (the visibility control lives inside it too).
  // Never yourself; anonymous viewers have no mailbox to send from.
  const messageButton =
    !viewerContext?.isSelf && !!viewerContext?.isAuthenticated ? (
      <MessageButton profileId={prProfile.id} openInboxThread iconClassName="fa-regular fa-message" />
    ) : null;

  const actions = editable ? (
    <>
      {messageButton}
      <ShareProfileButton url={canonicalProfileUrl} />
      <HeaderKebabMenu
        onEditProfile={() => setOpenEditModal("HEADER")}
        onEditVisibility={() => setOpenEditModal("HEADER")}
      />
    </>
  ) : (
    <>
      <FollowControl followableType="Id::Profile::Promoter" followableId={prProfile.id} />
      {messageButton}
      <ShareProfileButton url={canonicalProfileUrl} />
    </>
  );

  return (
    <PersonHeader
      avatar={avatar}
      identity={identity}
      actions={actions}
      banner={banner}
      avatarWrapperClassName="pr-avatar-frame"
    />
  );
}

export default PrPersonHeader;
